import { forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react';
import SvgIcon, { type SvgIconProps } from '@mui/material/SvgIcon';
import type { IconProps as TablerIconProps } from '@tabler/icons-react';

const ICON_STROKE_WIDTH = 1.75;

export type OutlineIconProps = SvgIconProps & { size?: number | string };
export type OutlineIconComponent = ReturnType<typeof createOutlineIcon>;

/** Keeps MUI sizing, theme colors, titles and refs with one Tabler SVG root. */
export function createOutlineIcon(
  Icon: ForwardRefExoticComponent<TablerIconProps & RefAttributes<SVGSVGElement>>,
  name: string,
) {
  const Root = forwardRef<SVGSVGElement, TablerIconProps & { ownerState?: unknown }>(
    ({ ownerState, ...props }, ref) => {
      void ownerState;
      return <Icon {...props} ref={ref} stroke={ICON_STROKE_WIDTH} fill="none" />;
    },
  );
  Root.displayName = `${name}Root`;

  const Component = forwardRef<SVGSVGElement, OutlineIconProps>(
    ({ size, sx, style, htmlColor, color, ...props }, ref) => (
      <SvgIcon
        {...props}
        ref={ref}
        color={color}
        htmlColor={htmlColor}
        component={Root}
        viewBox="0 0 24 24"
        sx={[
          ...(htmlColor && (!color || color === 'inherit') ? [{ color: htmlColor }] : []),
          ...(size === undefined ? [] : [{ fontSize: size }]),
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          { '&&': { fill: 'none', stroke: 'currentColor', strokeWidth: ICON_STROKE_WIDTH } },
        ]}
        style={{ ...style, fill: 'none', stroke: 'currentColor', strokeWidth: ICON_STROKE_WIDTH }}
        data-icon-family="tabler-outline"
      />
    ),
  );
  Component.displayName = name;
  return Object.assign(Component, { muiName: 'SvgIcon' });
}
